/**
 * Customer Screen Monitor Configuration Utility
 * Helps manage dual-monitor setup for customer display
 * Uses Window Management API for accurate screen detection when available
 */

import Swal from "sweetalert2";

class CustomerScreenMonitor {
  constructor() {
    this.storageKey = "customerMonitorConfig";
    this.defaultConfig = {
      autoDetect: true,
      monitorIndex: 1, // 0 = primary, 1 = secondary
      position: "right", // 'left', 'right', 'above', 'below'
      fullscreen: true,
      showInstructions: true,
      autoOpenOnStartup: false, // auto-open on dual-screen setups when app loads
    };
    this.cachedScreens = null;
  }

  /**
   * Get current monitor configuration
   */
  getConfig() {
    try {
      const saved = localStorage.getItem(this.storageKey);
      return saved
        ? { ...this.defaultConfig, ...JSON.parse(saved) }
        : this.defaultConfig;
    } catch (error) {
      console.warn("Failed to get monitor config:", error);
      return this.defaultConfig;
    }
  }

  /**
   * Save monitor configuration
   */
  saveConfig(config) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(config));
      return true;
    } catch (error) {
      console.warn("Failed to save monitor config:", error);
      return false;
    }
  }

  /**
   * Check if Window Management API is available
   */
  isWindowManagementSupported() {
    return "getScreenDetails" in window;
  }

  /**
   * Request permission for window management
   * Returns true if permission granted, false otherwise
   */
  async requestWindowManagementPermission() {
    try {
      // Check if the permission API is available
      if ("permissions" in navigator) {
        const result = await navigator.permissions.query({
          name: "window-management",
        });
        if (result.state === "granted") {
          return true;
        }
      }

      // Try to get screen details, which will trigger permission prompt
      if (this.isWindowManagementSupported()) {
        await window.getScreenDetails();
        return true;
      }

      return false;
    } catch (error) {
      console.warn("Window management permission request failed:", error);
      return false;
    }
  }

  /**
   * Detect available monitors using Window Management API (async)
   * Falls back to legacy detection if not available
   */
  async detectMonitorsAsync() {
    // Try modern Window Management API first
    if (this.isWindowManagementSupported()) {
      try {
        const screenDetails = await window.getScreenDetails();

        const screens = screenDetails.screens.map((screen, index) => ({
          id: index,
          isPrimary: screen.isPrimary,
          width: screen.width,
          height: screen.height,
          availWidth: screen.availWidth,
          availHeight: screen.availHeight,
          left: screen.left,
          top: screen.top,
          label: screen.label || `Monitor ${index + 1}`,
          devicePixelRatio: screen.devicePixelRatio || 1,
        }));

        // Cache the screens for later use
        this.cachedScreens = screens;

        console.log("Window Management API: Detected screens:", screens);
        return screens;
      } catch (error) {
        console.warn("Window Management API failed:", error);
        // Fall through to legacy detection
      }
    }

    // Fallback to legacy detection
    return this.detectMonitors();
  }

  /**
   * Legacy monitor detection (synchronous)
   * Limited to current screen info only
   */
  detectMonitors() {
    try {
      const screens = [];

      // Get primary screen info
      screens.push({
        id: 0,
        isPrimary: true,
        width: window.screen.width,
        height: window.screen.height,
        availWidth: window.screen.availWidth || window.screen.width,
        availHeight: window.screen.availHeight || window.screen.height,
        left:
          window.screen.availLeft !== undefined ? window.screen.availLeft : 0,
        top: window.screen.availTop !== undefined ? window.screen.availTop : 0,
        label: "Primary Monitor",
      });

      // Check for saved secondary screen config
      const savedConfig = this.getConfig();
      if (savedConfig.secondaryScreen) {
        screens.push({
          id: 1,
          isPrimary: false,
          ...savedConfig.secondaryScreen,
          label:
            savedConfig.secondaryScreen.label || "Secondary Monitor (Manual)",
        });
      }

      return screens;
    } catch (error) {
      console.warn("Monitor detection failed:", error);
      return [
        {
          id: 0,
          isPrimary: true,
          width: window.screen.width,
          height: window.screen.height,
          left: 0,
          top: 0,
          label: "Primary Monitor",
        },
      ];
    }
  }

  /**
   * Get optimal window configuration for customer screen (async)
   * Uses Window Management API for accurate positioning
   */
  async getCustomerWindowConfigAsync() {
    const config = this.getConfig();

    // Try to detect monitors with the new async method
    const monitors = await this.detectMonitorsAsync();

    console.log("Detected monitors:", monitors.length, monitors);

    // Find non-primary monitor
    const secondaryMonitor = monitors.find((m) => !m.isPrimary);

    // If we have a secondary monitor detected, use it
    if (secondaryMonitor) {
      console.log(
        "Using detected secondary monitor:",
        secondaryMonitor.label,
        "at position",
        secondaryMonitor.left,
        secondaryMonitor.top
      );
      return {
        left: secondaryMonitor.left,
        top: secondaryMonitor.top,
        width: secondaryMonitor.availWidth || secondaryMonitor.width,
        height: secondaryMonitor.availHeight || secondaryMonitor.height,
        fullscreen: config.fullscreen,
        hasSecondaryScreen: true,
        screenLabel: secondaryMonitor.label,
      };
    }

    // If only primary detected, use configured position to estimate secondary screen
    // This is important for iPad/Sidecar displays that may not be auto-detected
    if (monitors.length === 1) {
      const primaryScreen = monitors[0];
      const position = config.position || "right";

      console.log(
        `Only primary monitor detected, using configured position: ${position}`
      );

      // Calculate position based on configured setting
      let left = 0;
      let top = 0;
      let width = primaryScreen.width;
      let height = primaryScreen.height;

      switch (position) {
        case "left":
          // iPad is to the left of primary
          left = -primaryScreen.width;
          top = 0;
          break;
        case "right":
          // iPad is to the right of primary (most common)
          left = primaryScreen.width;
          top = 0;
          break;
        case "above":
          // iPad is above primary
          left = 0;
          top = -primaryScreen.height;
          break;
        case "below":
          // iPad is below primary
          left = 0;
          top = primaryScreen.height;
          break;
        default:
          left = primaryScreen.width;
          top = 0;
      }

      console.log(`Estimated secondary position: left=${left}, top=${top}`);

      return {
        left,
        top,
        width,
        height,
        fullscreen: config.fullscreen,
        hasSecondaryScreen: false, // Not auto-detected
        screenLabel: `Secondary Monitor (${position})`,
      };
    }

    // Fallback: use primary monitor
    console.log("No secondary monitor detected, using primary");
    return {
      left: 0,
      top: 0,
      width: window.screen.width,
      height: window.screen.height,
      fullscreen: config.fullscreen,
      hasSecondaryScreen: false,
      screenLabel: "Primary Monitor",
    };
  }

  /**
   * Synchronous version for backward compatibility
   */
  getCustomerWindowConfig() {
    const config = this.getConfig();

    // Use cached screens if available
    const monitors = this.cachedScreens || this.detectMonitors();

    if (monitors.length === 1 || !config.autoDetect) {
      // Single monitor or manual configuration
      return {
        left: config.monitorIndex === 0 ? 0 : window.screen.width,
        top: 0,
        width: window.screen.width,
        height: window.screen.height,
        fullscreen: config.fullscreen,
      };
    }

    // Multiple monitors - use configured secondary monitor
    const customerMonitor =
      monitors.find((m) => !m.isPrimary) ||
      monitors.find((m) => m.id === config.monitorIndex) ||
      monitors[1];

    return {
      left: customerMonitor.left,
      top: customerMonitor.top,
      width: customerMonitor.availWidth || customerMonitor.width,
      height: customerMonitor.availHeight || customerMonitor.height,
      fullscreen: config.fullscreen,
    };
  }

  /**
   * Show configuration dialog for merchants
   */
  async showConfigurationDialog() {
    const config = this.getConfig();

    // Try to detect monitors async for better results
    let monitors;
    let apiSupported = this.isWindowManagementSupported();

    if (apiSupported) {
      try {
        monitors = await this.detectMonitorsAsync();
      } catch (error) {
        monitors = this.detectMonitors();
        apiSupported = false;
      }
    } else {
      monitors = this.detectMonitors();
    }

    return new Promise((resolve) => {
      const monitorOptions = monitors.map((monitor) => ({
        value: monitor.id,
        text: `${monitor.label} (${monitor.width}x${monitor.height})${
          monitor.isPrimary ? " - Primary" : ""
        }`,
      }));

      // Build status message based on API support
      const statusMessage = apiSupported
        ? `<p style="font-size: 12px; color: #4caf50; margin-top: 15px;">
            ✅ <strong>Window Management API Supported</strong> - Screens detected automatically.
          </p>`
        : `<p style="font-size: 12px; color: #ff9800; margin-top: 15px;">
            ⚠️ <strong>Manual Configuration Required</strong> - Your browser doesn't support automatic screen detection.
            Drag the customer screen window to your second monitor.
          </p>`;

      const manualConfigSection = !apiSupported
        ? `
        <div style="margin-bottom: 20px; padding: 10px; background: #fff3cd; border-radius: 8px;">
          <p style="font-size: 13px; margin: 0; color: #856404;">
            <strong>Manual Setup:</strong><br>
            1. Open the customer screen<br>
            2. Drag the window to your second monitor<br>
            3. Press F11 for fullscreen
          </p>
        </div>
      `
        : "";

      // Create dialog content
      const dialogHtml = `
        <div style="text-align: left; min-width: 400px;">
          ${manualConfigSection}
          
          <div style="margin-bottom: 20px;">
            <label><strong>Customer Monitor:</strong></label>
            <select id="monitor-select" style="width: 100%; margin-top: 5px; padding: 8px; border-radius: 4px; border: 1px solid #ddd;">
              ${monitorOptions
                .map(
                  (opt) =>
                    `<option value="${opt.value}"${
                      opt.value === config.monitorIndex ? " selected" : ""
                    }>${opt.text}</option>`
                )
                .join("")}
            </select>
          </div>

          <div style="margin-bottom: 20px; padding: 15px; background: #e3f2fd; border-radius: 8px; border: 1px solid #90caf9;">
            <label><strong>📱 iPad/Secondary Screen Position:</strong></label>
            <p style="font-size: 12px; color: #1565c0; margin: 5px 0 10px 0;">Where is your iPad/secondary screen relative to your main monitor?</p>
            <select id="position-select" style="width: 100%; padding: 10px; border-radius: 4px; border: 1px solid #90caf9; font-size: 14px;">
              <option value="right"${
                config.position === "right" ? " selected" : ""
              }>➡️ Right of main screen (most common)</option>
              <option value="left"${
                config.position === "left" ? " selected" : ""
              }>⬅️ Left of main screen</option>
              <option value="above"${
                config.position === "above" ? " selected" : ""
              }>⬆️ Above main screen</option>
              <option value="below"${
                config.position === "below" ? " selected" : ""
              }>⬇️ Below main screen</option>
            </select>
          </div>

          <div style="margin-bottom: 20px; padding: 12px; background: #e8f5e9; border-radius: 8px; border: 1px solid #a5d6a7;">
            <label style="display: flex; align-items: center; gap: 8px; cursor: pointer;">
              <input type="checkbox" id="auto-open-startup" ${
                config.autoOpenOnStartup ? "checked" : ""
              }>
              <span>
                <strong>Auto-open on startup (dual screen)</strong><br>
                <span style="font-size: 12px; color: #388e3c;">Automatically open customer screen when a second monitor is detected on app load</span>
              </span>
            </label>
          </div>

          <div style="margin-bottom: 20px;">
            <label style="display: flex; align-items: center; gap: 8px;">
              <input type="checkbox" id="auto-detect" ${
                config.autoDetect ? "checked" : ""
              }>
              Auto-detect secondary monitor
            </label>
          </div>

          <div style="margin-bottom: 20px;">
            <label style="display: flex; align-items: center; gap: 8px;">
              <input type="checkbox" id="fullscreen" ${
                config.fullscreen ? "checked" : ""
              }>
              Open in fullscreen mode
            </label>
          </div>

          <div style="margin-bottom: 20px;">
            <label style="display: flex; align-items: center; gap: 8px;">
              <input type="checkbox" id="show-instructions" ${
                config.showInstructions ? "checked" : ""
              }>
              Show help instructions
            </label>
          </div>

          ${statusMessage}

          <p style="font-size: 12px; color: #666; margin-top: 10px;">
            💡 <strong>Tip:</strong> For iPad with Sidecar, check System Settings → Displays to see your iPad's position.
          </p>
        </div>
      `;

      // Use SweetAlert for dialog
      if (Swal) {
        Swal.fire({
          title: "Customer Screen Settings",
          html: dialogHtml,
          showCancelButton: true,
          confirmButtonText: "Save Settings",
          cancelButtonText: "Cancel",
          width: 520,
          customClass: "ps-confirm-swal",
          preConfirm: () => {
            const newConfig = {
              autoOpenOnStartup: document.getElementById("auto-open-startup").checked,
              autoDetect: document.getElementById("auto-detect").checked,
              monitorIndex: parseInt(
                document.getElementById("monitor-select").value
              ),
              fullscreen: document.getElementById("fullscreen").checked,
              showInstructions:
                document.getElementById("show-instructions").checked,
              position: document.getElementById("position-select").value,
            };

            this.saveConfig(newConfig);
            return newConfig;
          },
        }).then((result) => {
          if (result.isConfirmed) {
            resolve(result.value);
          } else {
            resolve(null);
          }
        });
      } else {
        console.warn("SweetAlert2 not available for customer screen settings dialog");
        resolve(null);
      }
    });
  }

  /**
   * Open customer screen with proper positioning
   * Uses async detection for best results
   */
  async openCustomerScreen(customerScreenPath = "/customer-screen") {
    try {
      // Request permission first
      await this.requestWindowManagementPermission();

      // Get window config using async method
      const config = await this.getCustomerWindowConfigAsync();

      const features = [
        `left=${config.left}`,
        `top=${config.top}`,
        `width=${config.width}`,
        `height=${config.height}`,
        "fullscreen=yes",
        "titlebar=no",
        "location=no",
        "menubar=no",
        "scrollbars=no",
        "resizable=no",
        "status=no",
        "toolbar=no",
      ].join(",");

      console.log("Opening customer screen with config:", config);
      console.log("Window features:", features);

      const customerWindow = window.open(
        customerScreenPath,
        "customerScreen",
        features
      );

      if (customerWindow && !customerWindow.closed) {
        // Store reference to the window
        this.customerWindow = customerWindow;

        // Try to move the window to exact position (works in some browsers)
        try {
          customerWindow.moveTo(config.left, config.top);
          customerWindow.resizeTo(config.width, config.height);
        } catch (moveError) {
          console.log("Could not programmatically move window:", moveError);
        }

        return {
          success: true,
          window: customerWindow,
          config: config,
        };
      }

      return {
        success: false,
        error: "Window blocked or closed",
      };
    } catch (error) {
      console.error("Failed to open customer screen:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Test customer screen opening (legacy method)
   */
  testCustomerScreen() {
    const config = this.getCustomerWindowConfig();
    const features = [
      `left=${config.left}`,
      `top=${config.top}`,
      `width=${config.width}`,
      `height=${config.height}`,
      config.fullscreen ? "fullscreen=yes" : "fullscreen=no",
      "titlebar=no",
      "location=no",
      "menubar=no",
      "scrollbars=no",
    ].join(",");

    return window.open("/customer-screen", "customerScreenTest", features);
  }
}

export default new CustomerScreenMonitor();
